import React, { useCallback, useEffect, useState } from 'react';
import Router from './routes';
import jwtDecode from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import './assets/styles/main.scss';
import { toast } from 'react-toastify';
import { checkWebpSupport } from './helpers/utils';
import { init } from '@amplitude/analytics-browser';
import { identify, Identify, setUserId } from '@amplitude/analytics-browser';
import { AmplitudeKey } from './constants/configuration'; // , CrazyEgg
import RocketLogContainer from './containers/RocketLogContainer';
import { useLogRocket } from './helpers/useLogRocket';
import { useLogout } from './helpers/hooks/commonHooks';
import ChooseUserContainer from './containers/ChooseUserContainer';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { fetchVideoConfiguration } from '../features/video-configuration/videoConfigurationSlice';
import { getProfile, getUserProfile } from '../features/user/userSlice';
import { getStatusParentControl } from '../features/parentControl/parentControlSlice';
import {
  refreshToken,
  selectAuthInfo,
  setAuthorized,
  setAuthorizedData,
  getAccessToken
} from '../features/auth/authSlice';
import { fetchBanners } from '../features/banners/bannerSlice';
import { authWithToken, checkIsAuth } from '../api/authApi';

const App = () => {
  const { i18n } = useTranslation();
  const { isAuthorized } = useSelector(selectAuthInfo);
  const { logoutHandle } = useLogout();
  const dispatch = useDispatch();
  const accessToken = useSelector(getAccessToken);
  const [isAuthBootstrapCompleted, setIsAuthBootstrapCompleted] = useState(false);
  const { name, id, email, location, billing_id, groups, phone } = useSelector(getUserProfile);
  const { isShowRocketLogMode, setUserInfo } = useLogRocket();
  const [showPopup, setShowPopup] = useState(false);
  const [tokenUser, setTokenUser] = useState(null);
  const [tokenCredentials, setTokenCredentials] = useState(null);

  useEffect(() => {
    unwrapResult(dispatch(fetchBanners()));
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      setUserInfo({ id, name, email });
    }
  }, [email, id, name, setUserInfo]);
  useEffect(() => {
    const identifyObj = new Identify();
    if (id) {
      setUserId(id);
      identifyObj.setOnce('UserName', name);
      identifyObj.setOnce('UserId', id);
      identifyObj.setOnce('UserLogin', `${email}, ${phone}`);
      identifyObj.setOnce('location', location);
      identifyObj.setOnce('BillingId', billing_id);
      identifyObj.setOnce('AccountType', groups?.[0].name);
    }
    identify(identifyObj);
  }, [billing_id, email, groups, id, location, name, phone]);

  useEffect(() => {
    toast.configure();
    const e = document.documentElement;
    checkWebpSupport().then((isWebpSupported) => {
      isWebpSupported &&
        (0 <= e.className.indexOf('no-webp')
          ? (e.className = e.className.replace(/\bno-webp\b/, 'webp'))
          : (e.className += ' webp'));
    });
    if (navigator.userAgent.indexOf('Win') !== -1) {
      e.classList.add('scrollbar-thin');
    }
  }, []);

  useEffect(() => {
    // init Amplitude SDK
    AmplitudeKey && init(AmplitudeKey, null, { minIdLength: 1 });
  }, []);

  // useEffect(() => {
  //   if (CrazyEgg) {
  //     const script = document.createElement('script');
  //     script.src = '//script.crazyegg.com/pages/scripts/0068/4662.js';
  //     script.async = true;
  //     script.type = 'text/javascript';
  //     document.head.appendChild(script);
  //   }
  // }, []);

  useEffect(() => {
    (async () => {
      try {
        const storedAccessToken = localStorage.getItem('access_token');
        const storedRefreshToken = localStorage.getItem('refresh_token');
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token')?.trim();

        dispatch(
          setAuthorizedData({ access_token: storedAccessToken, refresh_token: storedRefreshToken })
        );

        // Определяем, авторизован ли пользователь по текущей сессии.
        let isUserAuthorized = false;

        if (storedAccessToken) {
          try {
            await checkIsAuth();
            isUserAuthorized = true;
          } catch (err) {
            // access_token невалиден/просрочен — пробуем обновить по refresh_token.
          }
        }

        const refreshTokenValue = localStorage.getItem('refresh_token');
        if (!isUserAuthorized && refreshTokenValue) {
          try {
            await unwrapResult(await dispatch(refreshToken({ refresh_token: refreshTokenValue })));
            isUserAuthorized = true;
          } catch (err) {
            // refresh_token невалиден.
          }
        }

        // Нет ?token — стандартный флоу.
        if (!token) {
          if (isUserAuthorized) {
            dispatch(setAuthorized(true));
          } else {
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('access_token');
            dispatch(setAuthorizedData(null));
            dispatch(setAuthorized(false));
          }
          return;
        }

        // Есть ?token и пользователь уже авторизован.
        // Узнаём, какому пользователю принадлежит токен, и показываем попап,
        // только если это другой пользователь.
        if (isUserAuthorized) {
          dispatch(setAuthorized(true));

          try {
            // Текущий пользователь — из claim `sub` access-токена (без лишнего запроса).
            const currentUserId = jwtDecode(localStorage.getItem('access_token'))?.sub;

            const tokenAuth = await authWithToken({ token });
            const tokenUserId = tokenAuth?.data?.user?.id;

            if (tokenUserId && currentUserId && String(tokenUserId) !== String(currentUserId)) {
              setTokenUser(tokenAuth?.data?.user);
              setTokenCredentials({
                access_token: tokenAuth?.data?.access_token,
                refresh_token: tokenAuth?.data?.refresh_token
              });
              setShowPopup(true);
            }
          } catch (err) {
            // Невалидный ?token не должен ломать текущую сессию — игнорируем.
          }
          return;
        }

        // Есть ?token и пользователь не авторизован — авторизуемся по токену.
        const res = await authWithToken({ token });
        const { access_token, refresh_token } = res?.data || {};
        if (!access_token || !refresh_token) {
          throw new Error('Invalid authWithToken response: missing tokens');
        }
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);

        dispatch(setAuthorizedData({ access_token, refresh_token }));
        dispatch(setAuthorized(true));
      } catch (err) {
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('access_token');
        dispatch(setAuthorizedData(null));
        dispatch(setAuthorized(false));
      } finally {
        setIsAuthBootstrapCompleted(true);
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthBootstrapCompleted || !isAuthorized || !accessToken) {
      return;
    }

    let timeout;
    const maxTimeOutTime = 2147483647;

    try {
      const decodedToken = jwtDecode(accessToken);
      let millisecondsForExpired = decodedToken.exp * 1000 - Date.now() - 120000;
      if (millisecondsForExpired < 0) {
        millisecondsForExpired = 0;
      }
      const timeoutTime =
        millisecondsForExpired > maxTimeOutTime ? maxTimeOutTime : millisecondsForExpired;

      timeout = window.setTimeout(async () => {
        try {
          await unwrapResult(
            await dispatch(refreshToken({ refresh_token: localStorage.getItem('refresh_token') }))
          );
        } catch (err) {
          logoutHandle();
        }
      }, timeoutTime);
    } catch (err) {
      logoutHandle();
    }

    return () => {
      window.clearTimeout(timeout);
    };
  }, [accessToken, dispatch, isAuthBootstrapCompleted, isAuthorized, logoutHandle]);

  /**
   * Fetch video configuration
   * It is needed to build associations between the category identifier, name, genre.
   */
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        isMounted && unwrapResult(await dispatch(fetchVideoConfiguration()));
      } catch (e) {
        console.log(e);
      }
    })();

    return () => (isMounted = false);
  }, [dispatch, i18n.language, isAuthorized]);

  /**
   * Get profile data when user is authorized
   */
  useEffect(() => {
    (async () => {
      try {
        isAuthorized && (await unwrapResult(await dispatch(getProfile())));
      } catch (e) {
        console.log(e);
      }
    })();
  }, [dispatch, isAuthorized]);

  useEffect(() => {
    (async () => {
      try {
        isAuthorized && unwrapResult(await dispatch(getStatusParentControl()));
      } catch (e) {
        console.log(e);
      }
    })();
  }, [dispatch, isAuthorized]);

  // useEffect(() => {
  //   const isAgeConfirmed = localStorage.getItem('ageConfirmed');

  //   if (!isAgeConfirmed) {
  //     setShowPopup(true);
  //   } else {
  //     setShowPopup(false);
  //   }
  // }, []);

  // Применяем токены нового пользователя и перезагружаем приложение на '/',
  // чтобы профиль и остальные данные перезапросились под него.
  const handleApplyTokenUser = useCallback(() => {
    if (!tokenCredentials?.access_token || !tokenCredentials?.refresh_token) {
      return;
    }
    localStorage.setItem('access_token', tokenCredentials.access_token);
    localStorage.setItem('refresh_token', tokenCredentials.refresh_token);
    window.location.replace('/');
  }, [tokenCredentials]);

  return (
    <React.StrictMode>
      <Router />
      <RocketLogContainer isShow={isShowRocketLogMode} />
      {/* {showPopup ? <AgeConfirmationContainer onModalCloseHandler={closeModalAgeConfirmation} /> : null} */}
      {showPopup ? (
        <ChooseUserContainer
          user={tokenUser}
          onApplyHandler={handleApplyTokenUser}
          onModalCloseHandler={() => setShowPopup(false)}
        />
      ) : null}
    </React.StrictMode>
  );
};

export default App;
